import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useDeviceType } from '@cian/ui-kit';

import { isTagsScreen, useSearchModalState } from './helpers';
import { SearchModalCancelButtonContainer } from './parts/SearchModalCancelButton';
import { SearchModalContentContainer } from './parts/SearchModalContent';
import { SearchModalSubmitButtonContainer } from './parts/SearchModalSubmitButton';
import {
  ISearchModalSuggestInputContainerPublic,
  SearchModalSuggestInputContainer,
} from './parts/SearchModalSuggestInput';
import { trackPromoTagsSelectAction } from '../../actions/analytics';
import { promosApplyFiltersRequestAction } from '../../actions/filters';
import { resetJsonQuery } from '../../actions/jsonQuery';
import { suggestionsRequestAction, suggestionsReset } from '../../actions/suggestions';
import { resetTags } from '../../actions/tags';
import { SearchModalLayout } from '../../components/SearchModal';
import { MIN_SEARCH_INPUT_VALUE_LENGTH } from '../../constants/searchInput';
import { TThunkDispatch } from '../../types/redux/thunk';

export interface ISearchModalContainerProps {
  open: boolean;
  onClose: () => void;
}

export const SearchModalContainer: React.FC<ISearchModalContainerProps> = props => {
  const { open, onClose } = props;
  const [isInputFocused, setIsInputFocus] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const inputValueLength = inputValue.length;
  const deviceType = useDeviceType();
  const inputContainerRef = React.useRef<ISearchModalSuggestInputContainerPublic>(null);
  const dispatch: TThunkDispatch = useDispatch();
  const [state] = useSearchModalState({
    isInputFocused,
    inputValueLength,
  });

  const fetchSuggestions = React.useCallback(async (value: string) => {
    if (value.length >= MIN_SEARCH_INPUT_VALUE_LENGTH) {
      await dispatch(suggestionsRequestAction(value));
    }
  }, []);

  const clearInputValue = React.useCallback(() => {
    inputContainerRef.current?.clearValue();
  }, [inputContainerRef]);

  const handleInputChange = React.useCallback(
    async (value: string) => {
      setInputValue(value);

      await fetchSuggestions(value);
    },
    [setInputValue],
  );

  const handleClickSubmit = React.useCallback(async () => {
    try {
      setIsSubmitting(true);
      await dispatch(promosApplyFiltersRequestAction());
      clearInputValue();
      onClose();
    } catch {
      // ignore error
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, onClose]);

  const handleClickClose = React.useCallback(() => {
    onClose();
    dispatch(trackPromoTagsSelectAction());
    dispatch(resetJsonQuery());
    dispatch(resetTags());
    dispatch(suggestionsReset());
    clearInputValue();
  }, [onClose, dispatch]);

  const handleInputFocus = React.useCallback(async () => {
    setIsInputFocus(true);

    await fetchSuggestions(inputValue);
  }, [inputValue]);

  const handleSuggestionClick = React.useCallback(() => {
    setIsInputFocus(false);
    clearInputValue();
    dispatch(suggestionsReset());
  }, []);

  const handleInputClear = React.useCallback(() => {
    dispatch(suggestionsReset());
  }, []);

  return (
    <SearchModalLayout
      open={open}
      onClose={handleClickClose}
      showFooter={isTagsScreen(state)}
      content={<SearchModalContentContainer modalState={state} onSuggestionClick={handleSuggestionClick} />}
      input={
        <SearchModalSuggestInputContainer
          ref={inputContainerRef}
          disabled={isSubmitting}
          deviceType={deviceType}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClear={handleInputClear}
        />
      }
      submitButton={
        <SearchModalSubmitButtonContainer deviceType={deviceType} onClick={handleClickSubmit} loading={isSubmitting} />
      }
      cancelButton={<SearchModalCancelButtonContainer deviceType={deviceType} onClick={handleClickClose} />}
    />
  );
};
